/**
 * Reminder tools (RFC 0350 S3): an agent sets, lists and changes the
 * person's reminders on the World.
 *
 * A reminder wakes the World agent by default, or this session when the work
 * is this conversation's to pick back up (3c). When it comes due, the woken
 * agent gets a `mod.schedule_due` event and a turn, and decides what to do.
 *
 * WHO OWNS IT. The daemon holds no person id today, so a reminder is owned by
 * the World it is set in, and records the session that set it. RFC 0345's
 * member identity replaces the owner when sharing lands (RFC 0350 5).
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import type { ScheduleTiming } from '../model/world/schedule-time'
import {
  createSchedule,
  projectSchedule,
  projectScheduleDirectory,
  type ReminderSubscriber,
  type ScheduleRef,
  type ScheduleState,
  setScheduleState,
  updateSchedule,
} from '../model/world/schedules'
import { type ToolDef, toolArgString } from './project-tools'

/** This machine's zone: where the person is sitting when they ask. */
function hostTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

/** `[1, 3]`, `"mon,wed"`, or a list read off the log. */
function weekdaysArg(value: unknown): number[] | undefined {
  if (value === undefined || value === null) return undefined
  const items =
    typeof value === 'object' && Symbol.iterator in (value as object)
      ? Array.from(value as Iterable<unknown>)
      : String(toolArgString(value) ?? '').split(',')
  const days = items
    .map((v) => {
      const s = String(toolArgString(v) ?? v)
        .trim()
        .toLowerCase()
      if (/^\d$/.test(s)) return Number(s)
      const i = WEEKDAYS.indexOf(s.slice(0, 3))
      return i
    })
    .filter((d) => d >= 0 && d <= 6)
  return days.length ? days : undefined
}

/** A timing from tool arguments, or undefined when none was given. */
function timingArg(args: Record<string, unknown>): ScheduleTiming | undefined {
  const at = toolArgString(args.at)?.trim()
  const every = toolArgString(args.every)?.trim()
  if (at && every) throw new Error('give "at" or "every", not both')
  if (at) return { kind: 'once', at }
  if (!every) return undefined
  if (every !== 'day' && every !== 'week') {
    throw new Error('"every" is "day" or "week"')
  }
  const time = toolArgString(args.time)?.trim()
  if (!time) throw new Error('a repeating reminder needs "time", as HH:MM')
  return {
    kind: 'calendar',
    timeZone: toolArgString(args.timeZone)?.trim() || hostTimeZone(),
    rule: {
      every,
      time,
      ...(every === 'week' ? { weekdays: weekdaysArg(args.weekdays) } : {}),
    },
  }
}

function subscribersArg(
  value: unknown,
  sessionId: string | undefined,
): ReminderSubscriber[] | undefined {
  const wake = toolArgString(value)?.trim()
  if (!wake) return undefined
  if (wake === 'world-agent') {
    return [{ recipient: { kind: 'world-agent' }, delivery: 'wake' }]
  }
  if (wake === 'this-session') {
    if (!sessionId) throw new Error('there is no session to wake here')
    return [{ recipient: { kind: 'session', sessionId }, delivery: 'wake' }]
  }
  throw new Error('"wake" is "world-agent" or "this-session"')
}

function when(iso: string | undefined, timeZone: string): string {
  if (!iso) return 'nothing due'
  const local = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso))
  return `${local} ${timeZone} (${iso})`
}

function describe(s: ScheduleRef): string {
  const zone = s.timing.kind === 'calendar' ? s.timing.timeZone : hostTimeZone()
  const rule =
    s.timing.kind === 'once'
      ? 'once'
      : s.timing.rule.every === 'day'
        ? `every day at ${s.timing.rule.time}`
        : `every ${(s.timing.rule.weekdays ?? []).map((d) => WEEKDAYS[d]).join(', ')} at ${s.timing.rule.time}`
  const waker = s.target.subscribers.find((x) => x.delivery === 'wake')
  const wakes =
    waker?.recipient.kind === 'session'
      ? `session ${waker.recipient.sessionId}`
      : 'the World agent'
  const last = s.recent[s.recent.length - 1]
  return [
    `${s.scheduleId}  [${s.state}]  "${s.target.text}"`,
    `  ${rule}; next: ${when(s.nextAt, zone)}; wakes ${wakes}`,
    `  set by ${s.createdBy}` +
      (s.updatedBy && s.updatedBy !== s.createdBy
        ? `, last changed by ${s.updatedBy}`
        : '') +
      (last
        ? `; last: ${last.status} for ${last.dueAt}${last.note ? ` (${last.note})` : ''}`
        : ''),
  ].join('\n')
}

const TIMING_PROPERTIES = {
  at: {
    type: 'string',
    description:
      'For a one-time reminder: the date and time, ISO 8601 with an offset ' +
      '(e.g. 2026-10-20T09:00:00-04:00).',
  },
  every: {
    type: 'string',
    enum: ['day', 'week'],
    description:
      'For a repeating reminder. Give "time", and "weekdays" for week.',
  },
  time: { type: 'string', description: 'Local time, HH:MM, 24-hour.' },
  weekdays: {
    type: 'string',
    description: 'For every "week": days, e.g. "mon,thu".',
  },
  timeZone: {
    type: 'string',
    description:
      "IANA zone, e.g. America/New_York. Defaults to this machine's zone.",
  },
} as const

export function scheduleTools(
  repo: Repo,
  worldId: string | null | undefined,
  sessionId: string | undefined,
): Record<string, ToolDef> {
  if (!worldId) return {}
  const by = sessionId || 'agent'

  const load = async (id: string): Promise<ScheduleRef> => {
    const known = projectScheduleDirectory(
      (await repo.find(worldId as never)).doc(),
    ).some((e) => e.scheduleId === id)
    if (!known) throw new Error(`no reminder ${id} in this World`)
    const ref = projectSchedule(id, (await repo.find(id as never)).doc())
    if (!ref) throw new Error(`${id} is not a reminder`)
    return ref
  }

  return {
    create_reminder: {
      description:
        'Set a reminder for the person: once ("at") or repeating ("every" day ' +
        'or week at a local "time"). When it comes due, the agent it wakes ' +
        'gets the text and a turn, and decides what to do: tell the person, ' +
        'or pick the work up. It wakes the World agent unless wake is ' +
        '"this-session", for work this conversation should continue. Write ' +
        'the text as an instruction to that agent ("run the weekly review in ' +
        'project X", "remind Nassar to send the invoice"). Returns the ' +
        'reminder and when it is next due; tell the person.',
      inputSchema: {
        type: 'object',
        properties: {
          text: { type: 'string', description: 'What it is for.' },
          ...TIMING_PROPERTIES,
          wake: {
            type: 'string',
            enum: ['world-agent', 'this-session'],
            description: 'Who wakes when it is due. Default world-agent.',
          },
          catchUp: {
            type: 'string',
            enum: ['skip', 'latest'],
            description:
              'Repeating only: if no clock was awake when it came due, ' +
              '"skip" (default) moves on, "latest" runs once on return.',
          },
        },
        required: ['text'],
      },
      execute: async (args) => {
        const text = toolArgString(args.text)?.trim() ?? ''
        if (!text) throw new Error('text is required')
        const timing = timingArg(args)
        if (!timing) throw new Error('give "at", or "every" with "time"')
        const catchUp = toolArgString(args.catchUp)?.trim()
        const s = await createSchedule(repo, worldId, {
          owner: { kind: 'person', id: worldId },
          timing,
          target: {
            kind: 'reminder',
            text,
            ...(args.wake !== undefined
              ? { subscribers: subscribersArg(args.wake, sessionId) }
              : {}),
          },
          ...(catchUp === 'latest' ? { catchUp: 'latest' as const } : {}),
          by,
        })
        return `Set.\n${describe(s)}`
      },
    },
    list_reminders: {
      description:
        "The World's reminders: what each says, when it is next due, who it " +
        'wakes, and what happened last time. Cancelled ones are left out ' +
        'unless all is true.',
      inputSchema: {
        type: 'object',
        properties: {
          all: { type: 'boolean', description: 'Include cancelled ones.' },
        },
      },
      execute: async (args) => {
        const all = args.all === true || toolArgString(args.all) === 'true'
        const entries = projectScheduleDirectory(
          (await repo.find(worldId as never)).doc(),
        ).filter((e) => all || e.state !== 'cancelled')
        if (entries.length === 0) return 'No reminders.'
        const out: string[] = []
        for (const e of entries) {
          const ref = projectSchedule(
            e.scheduleId,
            (await repo.find(e.scheduleId as never)).doc(),
          )
          if (ref) out.push(describe(ref))
        }
        return out.join('\n\n')
      },
    },
    change_reminder: {
      description:
        'Change one reminder: pause, resume or cancel it (state), or change ' +
        'its text, its time ("at", or "every" with "time"), or who it wakes. ' +
        'Cancelling is final. Resuming skips whatever came due while paused.',
      inputSchema: {
        type: 'object',
        properties: {
          reminderId: {
            type: 'string',
            description: 'From create_reminder or list_reminders.',
          },
          state: { type: 'string', enum: ['active', 'paused', 'cancelled'] },
          text: { type: 'string' },
          ...TIMING_PROPERTIES,
          wake: { type: 'string', enum: ['world-agent', 'this-session'] },
        },
        required: ['reminderId'],
      },
      execute: async (args) => {
        const id = toolArgString(args.reminderId)?.trim() ?? ''
        if (!id) throw new Error('reminderId is required')
        let s = await load(id)
        const timing = timingArg(args)
        const text = toolArgString(args.text)?.trim()
        const subscribers = subscribersArg(args.wake, sessionId)
        if (timing || text || subscribers) {
          s = await updateSchedule(repo, worldId, id, {
            ...(timing ? { timing } : {}),
            ...(text ? { text } : {}),
            ...(subscribers ? { subscribers } : {}),
            by,
          })
        }
        const state = toolArgString(args.state)?.trim()
        if (state) {
          if (!['active', 'paused', 'cancelled'].includes(state)) {
            throw new Error('state is "active", "paused" or "cancelled"')
          }
          s = await setScheduleState(
            repo,
            worldId,
            id,
            state as ScheduleState,
            by,
          )
        }
        return `Changed.\n${describe(s)}`
      },
    },
  }
}
